import { FC, useEffect, useState, MouseEventHandler } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { connect } from 'react-redux';

import classnames from 'classnames';
import AlertBellIcon from 'dibs-icons/exports/AlertBell';
import { Link } from 'dibs-elements/exports/Link';
import styles from './Notifications-style.scss';
import { useUID } from 'dibs-uid';

import { LOCAL_STORAGE_KEY_NAME } from './Notifications';
import { wasPromoCodeNotificationShown } from '../../utils/promoCode.es';

import { NotificationsIcon_viewer as ViewerType } from './__generated__/NotificationsIcon_viewer.graphql';
import { NotificationsIcon_user as UserType } from './__generated__/NotificationsIcon_user.graphql';

type ReduxStoreType = {
    header: {
        isNavigationRefreshVariant: boolean;
    };
};

type Props = {
    viewer: ViewerType;
    user: UserType;
    lastUnreadNotificationDate: string;
    onClick: MouseEventHandler;
    onMouseEnter: MouseEventHandler;
    hasUnread?: boolean;
    isOpen: boolean;
    forceOpened: boolean;
    onActivePromotionFetched: (activePromotion: UserType['activePromotion']) => void;
    isNavigationRefreshVariant: boolean;
};
const messages = defineMessages({
    label: {
        id: 'dbl.Header.NotificationsIcon.label',
        defaultMessage: 'Notifications',
    },
    description: {
        id: 'dbl.Header.NotificationsIcon.description',
        defaultMessage: 'Unread messages',
    },
});

const hasNewNotification = (
    lastUnreadMessageDate?: string | null,
    lastUnreadNotificationDate?: string | null
): boolean => {
    const mostRecentMessageReceivedAt = lastUnreadMessageDate
        ? new Date(lastUnreadMessageDate).getTime()
        : 0;
    const mostRecentNotificationReceivedAt = lastUnreadNotificationDate
        ? new Date(lastUnreadNotificationDate).getTime()
        : 0;

    const mostRecent = Math.max(mostRecentMessageReceivedAt, mostRecentNotificationReceivedAt);
    const lastOpen = Number(localStorage.getItem(LOCAL_STORAGE_KEY_NAME)) || 0;
    return mostRecent > lastOpen;
};

const NotificationsIcon: FC<Props> = ({
    onClick,
    isOpen,
    onMouseEnter,
    viewer,
    user,
    onActivePromotionFetched,
    isNavigationRefreshVariant,
}) => {
    const uid = useUID() || undefined;
    const intl = useIntl();

    const { conversations, isAutomatedOfferNotificationDisabled } = viewer || {};
    const { activePromotion, activityFeed } = user || {};

    const notifications = activityFeed?.edges || [];
    const lastNotification = notifications.find(notification => {
        if (isAutomatedOfferNotificationDisabled) {
            const { type } = notification?.node || {};
            return type !== 'AUTOMATED_OFFER';
        } else {
            return true;
        }
    });
    const lastUnreadNotificationDate = lastNotification?.node?.eventDate;
    const lastUnreadMessageDate = conversations?.metadata?.lastUnreadMessageDate;
    const hasNew = hasNewNotification(lastUnreadMessageDate, lastUnreadNotificationDate);

    const hasUnread =
        hasNew || (activePromotion && !wasPromoCodeNotificationShown(activePromotion));

    /**
     * Since we can only @defer fragment spreads (and not fields), Notifications cannot fetch activePromotion while deferring it.
     * Here we're letting Notifications know that activePromotion has been fetched and is ready to use.
     */
    const [parentNotified, setParentNotified] = useState(false);
    useEffect(() => {
        if (!parentNotified && user.activePromotion) {
            onActivePromotionFetched(user.activePromotion);
            setParentNotified(true);
        }
    }, [user, onActivePromotionFetched, parentNotified, setParentNotified]);

    const iconClassName = classnames(styles.icon, {
        [styles.hasUnread]: hasUnread,
        [styles.navigationRefreshIcon]: isNavigationRefreshVariant,
    });

    return (
        <Link
            ariaLabel={intl.formatMessage(messages.label)}
            ariaDescribedBy={hasUnread ? uid : undefined}
            ariaExpanded={isOpen}
            ariaHasPopup
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            dataTn={`notifications-link${hasUnread ? '-unread' : ''}`}
            className={iconClassName}
        >
            {hasUnread && uid && (
                <span id={uid} className={styles.hidden}>
                    {intl.formatMessage(messages.description)}
                </span>
            )}
            <AlertBellIcon />
        </Link>
    );
};

const mapStateToProps = ({
    header,
}: ReduxStoreType): {
    isNavigationRefreshVariant: boolean;
} => {
    const { isNavigationRefreshVariant } = header;
    return {
        isNavigationRefreshVariant,
    };
};

export default createFragmentContainer(connect(mapStateToProps)(NotificationsIcon), {
    viewer: graphql`
        fragment NotificationsIcon_viewer on Viewer {
            conversations(filter: "showUnreadOnly") @include(if: $hasUserId) {
                metadata {
                    lastUnreadMessageDate
                }
            }
            isAutomatedOfferNotificationDisabled: isEnabled(
                key: "disableAutomatedOfferBellNotification"
            )
        }
    `,
    user: graphql`
        fragment NotificationsIcon_user on User {
            activePromotion {
                serviceId
            }
            activityFeed(
                first: 3
                elementTypes: [
                    PENDING_FEEDBACK
                    TRADE_REWARD
                    TRADE_REWARD_PRORATED
                    TRADE_REWARD_PROGRAM
                    AUTOMATED_OFFER
                ]
            ) {
                edges {
                    node {
                        type
                        eventDate
                    }
                }
            }
        }
    `,
});
